import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

export type BulkEditFieldType = 'text' | 'number' | 'select' | 'textarea';

export interface BulkEditField {
  /** Nom de la colonne SQL */
  key: string;
  /** Libellé affiché */
  label: string;
  type: BulkEditFieldType;
  /** Pour select uniquement */
  options?: { value: string; label: string }[];
  /** Hint sous l'input */
  hint?: string;
}

interface BulkEditDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Nom de la table Supabase (ex: 'operations', 'tiers', 'services') */
  table: string;
  /** Liste des champs modifiables en masse */
  fields: BulkEditField[];
  /** IDs des lignes à modifier */
  ids: string[];
  /** Appelé après succès */
  onApplied?: () => void;
}

/**
 * Modal générique de modification en masse.
 * - Utilisateur coche les champs à modifier
 * - Saisit la nouvelle valeur (commune à toutes les lignes)
 * - UPDATE WHERE id IN (...) côté Supabase, RLS appliquée
 * - Audit log généré automatiquement par les triggers
 */
export function BulkEditDialog({
  open,
  onOpenChange,
  table,
  fields,
  ids,
  onApplied,
}: BulkEditDialogProps): JSX.Element {
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setEnabled({});
      setValues({});
    }
  }, [open]);

  const activeFields = fields.filter((f) => enabled[f.key]);
  const ready = activeFields.length > 0 && activeFields.every((f) => values[f.key] !== undefined);

  const apply = async (): Promise<void> => {
    if (!ready || ids.length === 0) return;
    setSubmitting(true);
    try {
      const patch: Record<string, unknown> = {};
      for (const f of activeFields) {
        const raw = values[f.key];
        if (raw === undefined) continue;
        if (f.type === 'number') patch[f.key] = raw === '' ? null : Number(raw);
        else patch[f.key] = raw === '' ? null : raw;
      }
      // CHUNK update
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
      let success = 0;
      const errors: string[] = [];
      for (const chunk of chunks) {
        // Bypass typed client — table is dynamic.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const client = supabase as any;
        const { error, data } = await client.from(table).update(patch).in('id', chunk).select('id');
        if (error) errors.push(error.message);
        else success += (data ?? []).length;
      }
      if (errors.length === 0) {
        toast.success(`${success} ligne(s) mise(s) à jour`);
      } else {
        toast.warning(`${success} OK · ${errors.length} erreur(s) — ${errors[0]}`);
      }
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error('Erreur : ' + (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Modifier {ids.length} ligne(s) en masse</DialogTitle>
          <DialogDescription>
            Cochez les champs à modifier. Les champs non cochés gardent leur valeur d&apos;origine
            sur chaque ligne.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs">
            <strong>{activeFields.length}</strong> champ(s) à appliquer sur{' '}
            <strong>{ids.length}</strong> ligne(s).
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((f) => {
              const isOn = !!enabled[f.key];
              return (
                <div
                  key={f.key}
                  className="space-y-1.5 rounded-md border border-border p-3 transition-colors"
                  style={{ background: isOn ? 'hsl(var(--primary) / 0.05)' : undefined }}
                >
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={(e) => {
                        setEnabled((s) => ({ ...s, [f.key]: e.target.checked }));
                        if (!e.target.checked) {
                          setValues((s) => {
                            const cp = { ...s };
                            delete cp[f.key];
                            return cp;
                          });
                        } else if (values[f.key] === undefined) {
                          setValues((s) => ({ ...s, [f.key]: '' }));
                        }
                      }}
                      className="h-4 w-4 accent-primary"
                    />
                    {f.label}
                  </label>
                  {isOn && (
                    <>
                      {f.type === 'select' && (
                        <select
                          value={values[f.key] ?? ''}
                          onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                          className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="">— Choisir —</option>
                          {f.options?.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                      {f.type === 'text' && (
                        <Input
                          value={values[f.key] ?? ''}
                          onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                        />
                      )}
                      {f.type === 'number' && (
                        <Input
                          type="number"
                          step="0.01"
                          value={values[f.key] ?? ''}
                          onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                        />
                      )}
                      {f.type === 'textarea' && (
                        <textarea
                          value={values[f.key] ?? ''}
                          onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                          rows={2}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                      )}
                      {f.hint && (
                        <Label className="text-[10px] text-muted-foreground">{f.hint}</Label>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button disabled={!ready || submitting} onClick={() => void apply()}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Appliquer aux {ids.length} ligne(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
